// ABOUTME: i18n core — language resolution, translation lookup, and locale mapping.
// ABOUTME: German is the default; French via ?lang=fr. Data-driven: a 3rd language is one entry.

const SUPPORTED = ['de', 'fr'];

// All user-facing strings, keyed by a semantic id. Every `de` key MUST have an
// `fr` translation (guarded by a completeness test). {name} placeholders are
// filled by t(key, lang, params). French copy is a first pass — worth a native
// review before launch — except the locked decisions noted inline.
export const STRINGS = {
    de: {
        'meta.title': 'Hopp – Wimpel zur WM 2026',
        'meta.description': 'Hopp — Wimpel zur WM 2026 für die Schweizer Fans',
        'title.wm2026': 'WM 2026',
        'scene.title': 'WM 2026',
        'focus.plantConfirm': 'Hopp Schwiiz!',
        'toast.sessionLimit': 'Du hast deinen Wimpel bereits gepflanzt.',
        'onboarding.text': 'Tippe aufs Feld, um deinen Wimpel zu pflanzen',
        'error.webglNotSupported': 'Dein Browser unterstützt keine 3D-Grafik (WebGL).',
        'error.webglHint': 'Bitte verwende einen modernen Browser wie Chrome, Firefox oder Safari.',
        'error.loadFailed': 'Flaggen konnten nicht geladen werden. Bitte versuche es später erneut.',
        'error.rateLimit': 'Kurz warten, dann nochmal probieren',
        'error.network': 'Keine Verbindung — gleich nochmal versuchen',
        'counter.time': '[{time} Uhr]',
        'picker.title': 'Team auswählen',
        'picker.confirm': 'Bestätigen',
        'picker.cancel': 'Abbrechen',
        'picker.searchPlaceholder': 'Suchen…',
        'picker.teamsAriaLabel': 'WM 2026 Teams',
    },
    fr: {
        'meta.title': 'Hopp – Fanions pour la WM 2026',
        'meta.description': 'Hopp — Fanions pour la WM 2026 pour les fans suisses',
        'title.wm2026': 'WM 2026', // locked: kept identical in French
        'scene.title': 'WM 2026',  // locked
        'focus.plantConfirm': 'Allez la Suisse!', // locked
        'toast.sessionLimit': 'Tu as déjà planté ton fanion.',
        'onboarding.text': 'Touche le terrain pour planter ton fanion',
        'error.webglNotSupported': 'Ton navigateur ne supporte pas la 3D (WebGL).',
        'error.webglHint': 'Utilise un navigateur moderne comme Chrome, Firefox ou Safari.',
        'error.loadFailed': 'Les drapeaux n\'ont pas pu être chargés. Réessaie plus tard.',
        'error.rateLimit': 'Patiente un instant, puis réessaie',
        'error.network': 'Pas de connexion — réessaie bientôt',
        'counter.time': '[{time}]', // French has no "Uhr" suffix
        'picker.title': 'Choisir une équipe',
        'picker.confirm': 'Confirmer',
        'picker.cancel': 'Annuler',
        'picker.searchPlaceholder': 'Rechercher…',
        'picker.teamsAriaLabel': 'Équipes WM 2026',
    },
};

// BCP-47 locales for toLocaleString etc. — Swiss variants.
const LOCALES = { de: 'de-CH', fr: 'fr-CH' };

// Resolve the active language from the URL query (?lang=fr). Case-insensitive;
// unknown/missing falls back to `default` (itself defaulting to German).
export function resolveLang(search = '', { default: def = 'de' } = {}) {
    const params = new URLSearchParams(search ?? '');
    const raw = (params.get('lang') || '').toLowerCase();
    if (SUPPORTED.includes(raw)) return raw;
    return SUPPORTED.includes(def) ? def : 'de';
}

// Look up a translated string. Falls back to German when the language (or a
// specific key) is missing; returns the key itself if genuinely absent. Fills
// {name} placeholders from `params`.
export function t(key, lang = 'de', params = null) {
    const table = STRINGS[lang] || STRINGS.de;
    let str = key in table ? table[key] : STRINGS.de[key];
    if (str === undefined) return key;
    if (params) {
        str = str.replace(/\{(\w+)\}/g, (match, name) => (name in params ? String(params[name]) : match));
    }
    return str;
}

// Map a language to its BCP-47 locale (German by default).
export function resolveLocale(lang) {
    return LOCALES[lang] || LOCALES.de;
}
